// =============================================================================
// ui/auth/SignIn.tsx — email + password, and a magic-link fallback.
// -----------------------------------------------------------------------------
// ⚠ THIS WAS MIS-SEQUENCED, and the correction is worth recording where it will
// be read. The plan called the auth screens "not on the critical path" for Phase
// 4. That is wrong: Phase 4's per-tab gate is VISUAL PARITY against the old app
// on the same data, and nobody can review a tab they cannot open. Without this
// the new client renders exactly one screen — "not signed in" — to everyone,
// including the person doing the reviewing.
//
// The mistaken reasoning was that the beta session is shared. D5 shares the beta
// PROJECT, not the browser session: Supabase stores its session in localStorage,
// which is per-origin, so a session on beta.tradecoach.pro is invisible to
// localhost:5173 and to any future app.tradecoach.pro.
//
// Relocated from the old client, which offers both:
//   signInWithPassword   the normal path
//   signInWithOtp        magic link, for a forgotten password
//
// Sign-UP is deliberately NOT here. Account creation on the new client is a
// Phase 5 concern — it involves the signup hook that creates the preferences
// row, and creating accounts against the shared beta project from a half-ported
// client is a way to make rows nobody meant to make.
// =============================================================================

import { useRef, useState, type FormEvent } from 'react';
import { supabase } from '../../io/supabase/client';
import { reportFailure, reportFailureAs } from '../../io/diagnostics/errorLog';

type Mode = 'password' | 'link' | 'reset';
type Busy = false | 'submitting';

/**
 * 🚨 THE SAME ANSWER WHETHER OR NOT THE ACCOUNT EXISTS.
 *
 * A reset form that says "no account with that email" is an account-existence
 * oracle: anyone can test a list of addresses against it. Supabase's own
 * `resetPasswordForEmail` already answers neutrally, and this copy must not
 * undo that by reporting a difference the API deliberately withholds.
 *
 * ⚠ The July audit filed exactly this pair — an enumeration oracle AND a
 * mail-bomb — against `waitlist-signup`, and both are still open there. Same
 * family; not repeating it here.
 */
const RESET_SENT_COPY =
  'If there is an account for that address, a reset link is on its way. '
  + 'The link opens this app and expires — if it has, just ask for another.';

/**
 * Client-side throttle between reset requests.
 *
 * ⚠ NOT a security control — anyone can call the API directly, and Supabase's
 * own per-address rate limit is the real one. This exists so an honest user
 * hammering the button does not mail-bomb their own inbox, which is the failure
 * that actually happens.
 */
const RESET_THROTTLE_MS = 60_000;

export function SignIn() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<Busy>(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  /** When the last reset email was requested — see RESET_THROTTLE_MS. */
  const lastResetAt = useRef(0);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy('submitting');
    try {
      if (mode === 'password') {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        // ⚠ Surface Supabase's own message. "Invalid login credentials" and
        // "Email not confirmed" are different problems with different fixes, and
        // collapsing them into "Sign-in failed" sends a user to reset a password
        // that was never wrong.
        //
        // ⚠ `reportFailureAs`, not `reportFailure`, so both the screen and the log
        // read `.message` whatever shape comes back.
        //
        // 🔴 THE REASON THIS USED TO GIVE WAS FALSE. It said Supabase "returns
        // its own error shape rather than throwing, and a generic reader logs
        // `[object Object]`". Checked against the INSTALLED library 2026-08-27:
        // `AuthError extends Error` (auth-js/.../errors.js:22), so
        // `reportFailure` would have returned `e.message` correctly. The belief
        // came from THIS REPO'S TEST DOUBLES, which resolve bare
        // `{ message: '…' }` objects. Pinning is still right — it is correct
        // under both, and the doubles are what the suite exercises — but it is
        // belt-and-braces, not a rescue. See #52.
        if (err) setError(reportFailureAs('sign in', err, err.message));
        // No success branch: onAuthStateChange in App swaps the whole tree.
      } else if (mode === 'link') {
        const { error: err } = await supabase.auth.signInWithOtp({ email });
        if (err) setError(reportFailureAs('magic link', err, err.message));
        else setSent(true);
      } else {
        // ── Password reset ──────────────────────────────────────────────────
        const since = Date.now() - lastResetAt.current;
        if (since < RESET_THROTTLE_MS) {
          // ⚠ Still shows the SAME confirmation. Saying "wait 40 seconds" would
          // confirm the previous request went somewhere, which is the oracle
          // this flow is careful not to be.
          setSent(true);
          return;
        }
        lastResetAt.current = Date.now();
        // ⚠ redirectTo must land on THIS origin. The link is what carries the
        // recovery token, and sending it to beta would hand the session to the
        // other client — which has its own reset flow and its own bugs.
        await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}${window.location.pathname}`,
        });
        // 🚨 THE ERROR IS DELIBERATELY NOT SURFACED. See RESET_SENT_COPY: the
        // response must not differ by whether the address exists. A transport
        // failure is logged, because the user can retry and we should be able
        // to see it, but it does not change what they are told.
        setSent(true);
      }
    } catch (err) {
      // ⚠ The scope follows the MODE. One catch covers three different requests,
      // and a reset-email failure filed in the log under "sign in" points whoever
      // reads it at the wrong flow.
      const what = mode === 'reset' ? 'password reset email'
        : mode === 'link' ? 'magic link' : 'sign in';
      setError(reportFailure(what, err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="card">
        <h3>Check your email</h3>
        <p className="muted">
          {mode === 'reset' ? RESET_SENT_COPY : (
            <>A sign-in link is on its way to <b>{email}</b>. It opens this client directly.</>
          )}
        </p>
        <button type="button" className="btn" onClick={() => { setSent(false); setMode('password'); }}>
          Back
        </button>
      </div>
    );
  }

  return (
    <form className="card" onSubmit={(e) => void onSubmit(e)}>
      <h3>Sign in</h3>
      <p className="muted">
        Same account as the current client — this shares the beta database.
      </p>

      <div className="form-grid">
        <label className="field">
          <span className="field-label">Email</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {mode === 'password' ? (
          <label className="field">
            <span className="field-label">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        ) : null}
      </div>

      {error ? <p className="muted" role="alert">{error}</p> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" className="btn primary" disabled={busy !== false}>
          {busy ? 'Working…'
            : mode === 'password' ? 'Sign in'
              : mode === 'link' ? 'Email me a link'
                : 'Email me a reset link'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => { setMode(mode === 'password' ? 'link' : 'password'); setError(null); }}
        >
          {mode === 'password' ? 'Use a magic link' : 'Use a password'}
        </button>
        {/* 🚨 A REAL RESET, not "sign in by link and change it later". A magic
            link signs you in without touching the password, so the old one
            stays live — which is not what someone who has lost their password
            is asking for, and leaves them with two ways in when they wanted
            one. See ResetPassword.tsx. */}
        {mode === 'reset' ? null : (
          <button
            type="button"
            className="btn"
            onClick={() => { setMode('reset'); setError(null); }}
          >
            Forgot your password?
          </button>
        )}
      </div>
    </form>
  );
}
